import type { ScopeSectionSchema } from './types'

export const rulesSections: ScopeSectionSchema[] = [
  {
    id: 'rules_core',
    tabId: 'rules',
    formTitle: 'Rules & Matching',
    formDescription: 'Configure duplicate detection and blocklist matching rules',
    pdfTitle: 'Risk & Rules',
    pdfDescription: 'Duplicate/blocklist matching and risk configuration.',
    highLevelSummary: 'Risk & rules configuration.',
    groups: [
      {
        id: 'rules_tab_header',
        tabId: 'rules',
        sectionId: 'rules_core',
        formTitle: 'Rules & Matching',
        formDescription: 'Configure duplicate detection and blocklist matching rules',
        pdfTitle: 'Rules & Matching',
        pdfDescription: 'Duplicate detection and blocklist matching rules',
      },
    ],
    fields: [],
  },
  {
    id: 'rules_duplicate_matching',
    tabId: 'advanced',
    formTitle: 'Duplicate Matching Rules',
    formDescription: 'Configure how duplicate customers are detected.',
    pdfTitle: 'Duplicate Matching Rules',
    pdfDescription: 'Duplicate customer matching rules.',
    highLevelSummary: 'Duplicate matching rules selection.',
    groups: [
      {
        id: 'rules_duplicate_group',
        tabId: 'advanced',
        sectionId: 'rules_duplicate_matching',
        formTitle: 'Duplicate Matching Rules',
        helperText: 'Required for completion.',
        pdfTitle: 'Duplicate Matching Rules',
        pdfDescription: 'Duplicate customer matching rules.',
      },
    ],
    fields: [
      {
        id: 'rules_duplicate_rules_mode',
        tabId: 'advanced',
        sectionId: 'rules_duplicate_matching',
        groupId: 'rules_duplicate_group',
        path: 'rulesConfig.duplicateRules',
        valueType: 'select',
        formLabel: 'Duplicate rules',
        formDescription: 'Choose default rules, custom rules, or unknown.',
        tooltip: 'Duplicate detection identifies potential multiple accounts for the same person using fuzzy matching on name, DOB, address, email, and phone. Default rules use industry-standard scoring thresholds, while custom rules allow you to define specific matching criteria and weights.',
        pdfLabel: 'Duplicate rules mode',
        pdfDescription: 'Select Default or Custom duplicate rules.',
        contributesToCompletion: true,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: 'default', label: 'Use Default Rules' },
          { value: 'custom', label: 'Custom Rules' },
          { value: 'not_required', label: 'Not Required' },
        ],
      },
      {
        id: 'rules_custom_duplicate_rules',
        tabId: 'advanced',
        sectionId: 'rules_duplicate_matching',
        groupId: 'rules_duplicate_group',
        path: 'rulesConfig.customDuplicateRules',
        valueType: 'textarea',
        formLabel: 'Custom Duplicate Rules',
        formDescription: 'Describe custom duplicate matching rules.',
        tooltip: 'Specify your custom matching criteria, scoring weights, and thresholds. Include which fields to match on (name, DOB, address, contact details), fuzzy matching tolerances, and the minimum score threshold for flagging potential duplicates.',
        placeholder: 'Describe custom duplicate matching rules...',
        pdfLabel: 'Custom duplicate rules',
        pdfDescription: 'Provide the custom duplicate rules definition.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'rulesConfig.duplicateRules', equals: 'custom' }],
      },
    ],
  },
  {
    id: 'rules_blocklist_matching',
    tabId: 'advanced',
    formTitle: 'Blocklist Matching Rules',
    formDescription: 'Configure how blocklist hits are detected.',
    pdfTitle: 'Blocklist Matching Rules',
    pdfDescription: 'Blocklist matching rules.',
    highLevelSummary: 'Blocklist matching rules selection.',
    groups: [
      {
        id: 'rules_blocklist_group',
        tabId: 'advanced',
        sectionId: 'rules_blocklist_matching',
        formTitle: 'Blocklist Matching Rules',
        helperText: 'Required for completion.',
        pdfTitle: 'Blocklist Matching Rules',
        pdfDescription: 'Blocklist matching rules.',
      },
    ],
    fields: [
      {
        id: 'rules_blocklist_rules_mode',
        tabId: 'advanced',
        sectionId: 'rules_blocklist_matching',
        groupId: 'rules_blocklist_group',
        path: 'rulesConfig.blocklistRules',
        valueType: 'select',
        formLabel: 'Blocklist rules',
        formDescription: 'Choose custom rules, not required, or unknown.',
        tooltip: 'Match Lists (blocklists) let you check customer details against your own internal lists of blocked or flagged individuals. Custom rules let you define specific data types to match against and the action to take when matched.',
        pdfLabel: 'Blocklist rules mode',
        pdfDescription: 'Select Custom blocklist rules or Not Required.',
        contributesToCompletion: true,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: 'custom', label: 'Custom Rules' },
          { value: 'not_required', label: 'Not Required' },
        ],
      },
      {
        id: 'rules_custom_blocklist_rules',
        tabId: 'advanced',
        sectionId: 'rules_blocklist_matching',
        groupId: 'rules_blocklist_group',
        path: 'rulesConfig.customBlocklistRules',
        valueType: 'textarea',
        formLabel: 'Custom Blocklist Rules',
        formDescription: 'Describe custom blocklist matching rules.',
        tooltip: 'Define your custom blocklist matching criteria, including which fields to check (name, email, phone, device fingerprint, etc.), matching tolerances, and whether to use exact or fuzzy matching. Specify the action to take on a match (auto-reject, manual review, alert only).',
        placeholder: 'Describe custom blocklist matching rules...',
        pdfLabel: 'Custom blocklist rules',
        pdfDescription: 'Provide the custom blocklist rules definition.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'rulesConfig.blocklistRules', equals: 'custom' }],
      },
    ],
  },
  {
    id: 'rules_fraud_monitoring',
    tabId: 'rules',
    formTitle: 'Fraud & Transaction Monitoring',
    formDescription: 'Fraud and transaction monitoring requirements.',
    pdfTitle: 'Fraud & Transaction Monitoring',
    pdfDescription: 'Fraud and transaction monitoring requirements.',
    highLevelSummary: 'Fraud and monitoring requirements.',
    groups: [
      {
        id: 'rules_fraud_group',
        tabId: 'rules',
        sectionId: 'rules_fraud_monitoring',
        formTitle: 'Fraud & Transaction Monitoring',
        pdfTitle: 'Fraud & Transaction Monitoring',
        pdfDescription: 'Fraud and transaction monitoring requirements.',
      },
    ],
    fields: [
      {
        id: 'rules_fraud_enabled',
        tabId: 'rules',
        sectionId: 'rules_fraud_monitoring',
        groupId: 'rules_fraud_group',
        path: 'rulesConfig.fraudEnabled',
        valueType: 'select',
        formLabel: 'Fraud enabled',
        formDescription: 'Whether fraud checks are enabled.',
        tooltip: 'Fraud detection monitors for suspicious patterns, behaviors, and risk indicators during onboarding and ongoing activity. This includes device fingerprinting, velocity checks, behavioral analysis, and pattern recognition to identify potential fraudulent activity before it causes harm.',
        pdfLabel: 'Fraud enabled',
        pdfDescription: 'Whether fraud checks are enabled.',
        contributesToCompletion: false,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'rules_transaction_monitoring_enabled',
        tabId: 'rules',
        sectionId: 'rules_fraud_monitoring',
        groupId: 'rules_fraud_group',
        path: 'rulesConfig.transactionMonitoringEnabled',
        valueType: 'select',
        formLabel: 'Transaction Monitoring enabled',
        formDescription: 'Whether transaction monitoring is enabled (when fraud enabled).',
        tooltip: 'Transaction monitoring tracks and analyzes customer transactions for suspicious activity, unusual patterns, structuring attempts, and potential money laundering. This includes threshold alerts, velocity rules, geographic anomalies, and behavioral pattern analysis to detect financial crime in real-time.',
        pdfLabel: 'Transaction monitoring enabled',
        pdfDescription: 'Whether transaction monitoring is enabled.',
        contributesToCompletion: false,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        visibleWhen: [{ dependsOn: 'rulesConfig.fraudEnabled', equals: 'yes' }],
      },
      {
        id: 'rules_fraud_types',
        tabId: 'rules',
        sectionId: 'rules_fraud_monitoring',
        groupId: 'rules_fraud_group',
        path: 'rulesConfig.fraudTypes',
        valueType: 'multi_select',
        formLabel: 'Fraud Types',
        formDescription: 'Select the types of fraud detection required.',
        tooltip: 'Choose one or more fraud detection capabilities: Device Intelligence (device fingerprinting & risk scoring), Behavioural Biometrics (keystroke & interaction analysis), Phone Risk Assessment (phone number verification & risk), Email Risk Assessment (email address risk scoring), Transaction & Activity Monitoring (real-time transaction analysis).',
        pdfLabel: 'Fraud types',
        pdfDescription: 'Types of fraud detection enabled.',
        contributesToCompletion: false,
        options: [
          { value: 'device_intelligence', label: 'Device Intelligence' },
          { value: 'behavioural_biometrics', label: 'Behavioural Biometrics' },
          { value: 'phone_risk', label: 'Phone Risk Assessment' },
          { value: 'email_risk', label: 'Email Risk Assessment' },
          { value: 'transaction_monitoring', label: 'Transaction & Activity Monitoring' },
        ],
        visibleWhen: [{ dependsOn: 'rulesConfig.fraudEnabled', equals: 'yes' }],
      },
      {
        id: 'rules_fraud_requirements',
        tabId: 'rules',
        sectionId: 'rules_fraud_monitoring',
        groupId: 'rules_fraud_group',
        path: 'rulesConfig.fraudRequirements',
        valueType: 'textarea',
        formLabel: 'Fraud Requirements',
        formDescription: 'Capture fraud requirements.',
        tooltip: 'Document specific fraud detection requirements including risk thresholds, alert triggers, automated actions, manual review criteria, device fingerprinting needs, velocity rule specifications, and integration requirements with existing fraud prevention systems.',
        placeholder: 'Capture fraud requirements...',
        pdfLabel: 'Fraud requirements',
        pdfDescription: 'Fraud requirements notes.',
        contributesToCompletion: false,
      },
      {
        id: 'rules_transaction_monitoring_requirements',
        tabId: 'rules',
        sectionId: 'rules_fraud_monitoring',
        groupId: 'rules_fraud_group',
        path: 'rulesConfig.transactionMonitoringRequirements',
        valueType: 'textarea',
        formLabel: 'Transaction Monitoring Requirements',
        formDescription: 'Capture transaction monitoring requirements.',
        tooltip: 'Specify transaction monitoring rules including dollar amount thresholds, frequency limits, geographic restrictions, counterparty screening requirements, suspicious activity report (SAR) triggers, escalation workflows, and regulatory reporting obligations.',
        placeholder: 'Capture transaction monitoring requirements...',
        pdfLabel: 'Transaction monitoring requirements',
        pdfDescription: 'Transaction monitoring requirements notes.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'rulesConfig.fraudEnabled', equals: 'yes' }],
      },
    ],
  },
  {
    id: 'rules_risk_configuration',
    tabId: 'rules',
    formTitle: 'Risk Configuration',
    formDescription: 'Risk system and scoring configuration.',
    pdfTitle: 'Risk Configuration',
    pdfDescription: 'Risk system and scoring configuration.',
    highLevelSummary: 'Risk rating system selection and scoring settings.',
    groups: [
      {
        id: 'rules_risk_configuration_group',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        formTitle: 'Risk Configuration',
        pdfTitle: 'Risk Configuration',
        pdfDescription: 'Risk system and scoring configuration.',
      },
      {
        id: 'rules_risk_levels_group',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        formTitle: 'Risk Levels & Thresholds',
        pdfTitle: 'Risk levels',
        pdfDescription: 'Risk levels and thresholds.',
      },
      {
        id: 'rules_risk_matrix_group',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        formTitle: 'Risk Scoring Matrix',
        pdfTitle: 'Risk scoring matrix',
        pdfDescription: 'Risk scoring matrix.',
      },
      {
        id: 'rules_custom_risk_factors_group',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        formTitle: 'Custom Risk Factors (KVP)',
        pdfTitle: 'Custom risk factors',
        pdfDescription: 'Custom risk factors (KVP).',
      },
      {
        id: 'rules_risk_notes_group',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        formTitle: 'Risk Notes',
        pdfTitle: 'Risk notes',
        pdfDescription: 'Additional notes on risk configuration.',
      },
    ],
    fields: [
      {
        id: 'rules_risk_rating_system',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        groupId: 'rules_risk_configuration_group',
        path: 'rulesConfig.riskRatingSystem',
        valueType: 'select',
        formLabel: 'Risk rating system',
        formDescription: 'Which risk rating system should be used?',
        tooltip: 'Choose which risk assessment system will evaluate customer risk profiles. FrankieOne\'s system uses a weighted scoring model based on verification results, document authenticity, biometric matching, and behavioral signals. Client systems integrate your existing risk methodologies. Using both allows comparison and validation across different risk models.',
        pdfLabel: 'Risk rating system',
        pdfDescription: 'Risk rating system selection.',
        contributesToCompletion: false,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: 'frankieone', label: 'FrankieOne' },
          { value: 'theirs', label: "The Client's" },
          { value: 'both', label: 'Both' },
        ],
      },
      {
        id: 'rules_risk_system_change',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        groupId: 'rules_risk_configuration_group',
        path: 'rulesConfig.riskSystemChange',
        valueType: 'select',
        formLabel: 'Changing FrankieOne risk system',
        formDescription: 'Whether the customer is changing their risk system during implementation.',
        tooltip: 'Indicate if the customer is migrating from an existing risk assessment system to FrankieOne\'s platform. This affects implementation timelines, data migration requirements, parallel testing needs, and may require risk score mapping and historical data analysis to ensure consistent risk decisions during transition.',
        pdfLabel: 'Changing risk system',
        pdfDescription: 'Risk system change status.',
        contributesToCompletion: false,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: 'changing', label: 'Changing' },
          { value: 'not_changing', label: 'Not changing' },
        ],
      },
      {
        id: 'rules_risk_intro',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        groupId: 'rules_risk_configuration_group',
        path: 'riskConfig',
        valueType: 'text',
        formLabel: 'Risk intro (PDF)',
        pdfLabel: 'Risk intro',
        pdfDescription:
          'The below risk settings show the current points required for an entity to be referred as a fail, pass, or for review.\nYou can change the points required for each level, and configure the scores of each risk factor, which contribute to the overall risk level.\nYou can also request custom risk factors. Please contact FrankieOne for that.',
        contributesToCompletion: false,
      },
      {
        id: 'rules_risk_levels_table',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        groupId: 'rules_risk_levels_group',
        path: 'riskConfig.riskLevels',
        valueType: 'json',
        formLabel: 'Risk levels & thresholds (PDF)',
        tooltip: 'Define the point thresholds that determine whether a customer passes, requires review, or fails based on their cumulative risk score. These thresholds control automated decision-making and workflow routing. Lower thresholds mean stricter requirements; higher thresholds are more permissive.',
        pdfLabel: 'Risk levels & thresholds',
        pdfDescription: 'Risk levels and thresholds.',
        contributesToCompletion: false,
        defaultValue: ['Risk Level', 'Threshold', 'Rating', 'Status'],
      },
      {
        id: 'rules_risk_scoring_matrix_table',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        groupId: 'rules_risk_matrix_group',
        path: 'riskConfig.scoringMatrix',
        valueType: 'json',
        formLabel: 'Scoring matrix (PDF)',
        tooltip: 'Configure the point values assigned to each risk factor that contributes to the overall risk score. This matrix defines how verification outcomes, document quality, biometric matches, watchlist hits, and other signals are weighted. Adjust these values to align with your risk appetite and compliance requirements.',
        pdfLabel: 'Scoring matrix',
        pdfDescription: 'Risk scoring matrix.',
        contributesToCompletion: false,
        defaultValue: ['Factor Group', 'Review Factor', 'Criteria', 'Points'],
      },
      {
        id: 'rules_custom_risk_factors_summary',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        groupId: 'rules_custom_risk_factors_group',
        path: 'riskConfig.customFactors',
        valueType: 'json',
        formLabel: 'Custom risk factors (PDF)',
        tooltip: 'Define custom risk factors beyond FrankieOne\'s standard set to address industry-specific risks, regulatory requirements, or business logic unique to your use case. Custom factors can evaluate additional data points, apply specialized scoring rules, and integrate with external data sources or proprietary risk models.',
        pdfLabel: 'Custom risk factors',
        pdfDescription: 'Custom risk factors (KVP).',
        contributesToCompletion: false,
        defaultValue: ['Name', 'Type', 'Scoring', 'Applies to', 'Mappings'],
      },
      {
        id: 'rules_custom_risk_factors_empty_state',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        groupId: 'rules_custom_risk_factors_group',
        path: 'riskConfig.customFactors',
        valueType: 'text',
        formLabel: 'Custom risk factors empty state (PDF)',
        pdfLabel: 'Custom risk factors empty state',
        pdfDescription: 'No custom risk factors have been defined.',
        contributesToCompletion: false,
      },
      {
        id: 'rules_risk_notes',
        tabId: 'rules',
        sectionId: 'rules_risk_configuration',
        groupId: 'rules_risk_notes_group',
        path: 'riskConfig.notes',
        valueType: 'text',
        formLabel: 'Risk notes',
        pdfLabel: 'Risk notes',
        pdfDescription: 'Additional notes on risk configuration.',
        contributesToCompletion: false,
      },
    ],
  },
]
